import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, Clock, Stethoscope, Video, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import DoctorCard from '../components/DoctorCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appointments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated } = useAuth();
  
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [date, setDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [reason, setReason] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [myAppointments, setMyAppointments] = useState([]);

  useEffect(() => {
    fetchDoctors();
    if (isAuthenticated) {
      fetchMyAppointments();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const doctorId = searchParams.get('doctor');
    if (doctorId && doctors.length > 0) {
      const doctor = doctors.find(d => d.id === doctorId);
      if (doctor) {
        setSelectedDoctor(doctor);
      }
    }
  }, [searchParams, doctors]);

  useEffect(() => {
    if (selectedDoctor && date) {
      fetchAvailableSlots();
    }
  }, [selectedDoctor, date]);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/appointments/available-slots/${selectedDoctor.id}/${dateStr}`);
      setAvailableSlots(response.data);
      setSelectedSlot('');
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para agendar una cita');
      navigate('/login');
      return;
    }

    if (!selectedDoctor || !date || !selectedSlot) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        doctor_id: selectedDoctor.id,
        date: format(date, 'yyyy-MM-dd'),
        time: selectedSlot,
        reason,
        is_online: isOnline
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('¡Cita agendada exitosamente!');
      setSelectedDoctor(null);
      setDate(null);
      setSelectedSlot('');
      setReason('');
      setIsOnline(false);
      fetchMyAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agendar cita');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'confirmed': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'confirmed': return 'Confirmada';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container-app">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Booking Form */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Agendar Cita Médica</h1>
              <p className="text-slate-500 mt-1">Selecciona un doctor y agenda tu consulta</p>
            </div>

            {/* Doctors List */}
            {!selectedDoctor ? (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Doctores Disponibles</h2>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(2)].map((_, i) => (
                      <div key={i} className="card-doctor animate-pulse">
                        <div className="flex gap-4">
                          <div className="w-20 h-20 bg-slate-200 rounded-xl" />
                          <div className="flex-1">
                            <div className="h-5 bg-slate-200 rounded w-32 mb-2" />
                            <div className="h-4 bg-slate-200 rounded w-24" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : doctors.length === 0 ? (
                  <p className="text-slate-500">No hay doctores disponibles en este momento</p>
                ) : (
                  <div className="space-y-4">
                    {doctors.map((doctor) => (
                      <div
                        key={doctor.id}
                        onClick={() => doctor.is_available && setSelectedDoctor(doctor)}
                        className={`cursor-pointer transition-all ${
                          doctor.is_available ? 'hover:scale-[1.02]' : 'opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <DoctorCard doctor={doctor} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Booking Form */
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Selected Doctor */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">Doctor Seleccionado</h2>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      onClick={() => setSelectedDoctor(null)}
                      className="text-slate-500"
                    >
                      Cambiar
                    </Button>
                  </div>
                  <div className="flex items-center gap-4">
                    <img
                      src={selectedDoctor.image_url || 'https://via.placeholder.com/60'}
                      alt={selectedDoctor.name}
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                    <div>
                      <h3 className="font-semibold text-slate-900">{selectedDoctor.name}</h3>
                      <p className="text-sm text-[#2E7DCD]">{selectedDoctor.specialty}</p>
                      <p className="text-sm text-slate-500">Consulta: ${selectedDoctor.consultation_fee?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Date Selection */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <Label className="text-base font-semibold mb-4 block">Fecha de la Cita</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal input-default"
                        data-testid="date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP", { locale: es }) : "Selecciona una fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Selection */}
                {date && (
                  <div className="bg-white rounded-2xl p-6 border border-slate-200 animate-fadeIn">
                    <Label className="text-base font-semibold mb-4 block">Horario Disponible</Label>
                    {availableSlots.length === 0 ? (
                      <p className="text-slate-500">No hay horarios disponibles para esta fecha</p>
                    ) : (
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                        {availableSlots.map((slot) => (
                          <Button
                            key={slot}
                            type="button"
                            variant={selectedSlot === slot ? "default" : "outline"}
                            onClick={() => setSelectedSlot(slot)}
                            className={selectedSlot === slot ? "bg-[#2E7DCD]" : ""}
                            data-testid={`slot-${slot}`}
                          >
                            {slot}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Online Consultation Toggle */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5 text-[#2E7DCD]" />
                      <div>
                        <Label className="text-base font-medium">Consulta en Línea</Label>
                        <p className="text-sm text-slate-500">+$50.00 MXN</p>
                      </div>
                    </div>
                    <Switch
                      checked={isOnline}
                      onCheckedChange={setIsOnline}
                      data-testid="online-toggle"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <Label className="text-base font-semibold mb-4 block">Motivo de la Consulta (Opcional)</Label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Describe brevemente el motivo de tu consulta..."
                    className="input-default min-h-[100px]"
                    data-testid="reason-textarea"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={!date || !selectedSlot || submitting}
                  className="w-full btn-primary"
                  data-testid="submit-appointment"
                >
                  {submitting ? 'Agendando...' : 'Confirmar Cita'}
                </Button>
              </form>
            )}
          </div>

          {/* Right Column - My Appointments */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Mis Citas</h2>
            {!isAuthenticated ? (
              <div className="card-dashboard text-center py-8">
                <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">Inicia sesión para ver tus citas</p>
                <Button onClick={() => navigate('/login')} className="btn-secondary">
                  Iniciar Sesión
                </Button>
              </div>
            ) : myAppointments.length === 0 ? (
              <div className="card-dashboard text-center py-8">
                <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No tienes citas agendadas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myAppointments.slice(0, 5).map((appointment) => (
                  <div 
                    key={appointment.id} 
                    className="card-dashboard p-4"
                    data-testid={`appointment-${appointment.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(appointment.status)}`}>
                        {getStatusText(appointment.status)}
                      </span>
                      {appointment.is_online && (
                        <Video className="w-4 h-4 text-[#2E7DCD]" />
                      )}
                    </div>
                    <h4 className="font-medium text-slate-900">{appointment.doctor_name}</h4>
                    <p className="text-sm text-[#2E7DCD]">{appointment.specialty}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {appointment.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {appointment.time}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Appointments;
